import { useQuery, useQueryClient } from "@tanstack/react-query";
import { CircleCheck, TriangleAlert } from "lucide-react";
import { useEffect } from "react";
import { Link, useParams, useSearchParams } from "react-router";

import { useOrganization } from "@/auth/organization";
import { ErrorState, FullPageSpinner, Badge, buttonClass, Breadcrumb, Section } from "@/components/ui";
import { domainKeys, domainsApi, useDomainDetail, type DomainDetail, type Operation } from "@/lib/domains";

import { SecurityStatusRows } from "./security/shared";
import {
  DomainIcon,
  Heading,
  HeadTile,
  DomainPage,
  ProgressSection,
  Notice,
  type Step,
} from "@/components/domains/parts";

/** The DNSSEC operation this page follows: `?op=`, else the newest DNSSEC change on the domain. */
function latestDnssecOp(detail: DomainDetail): Operation | undefined {
  return detail.operations
    .filter((o) => o.kind === "dnssec_on" || o.kind === "dnssec_off")
    .sort((a, b) => b.created_at.localeCompare(a.created_at))[0];
}

/** Figma "Enable DNSSEC" in progress (4093-1255) and done (4093-1518). */
export function DnssecPage() {
  const { domainId } = useParams();
  const [params] = useSearchParams();
  const { orgId } = useOrganization();
  const qc = useQueryClient();
  const detail = useDomainDetail(domainId);
  const opId = params.get("op") ?? (detail.data ? latestDnssecOp(detail.data)?.id : undefined);

  const op = useQuery({
    queryKey: [...domainKeys.all(orgId), "operation", domainId ?? "", opId ?? ""],
    queryFn: () => domainsApi.operation(orgId, domainId!, opId!),
    enabled: Boolean(domainId && opId),
    refetchInterval: (q) => (q.state.data?.status === "pending" ? 3000 : false),
  });
  const status = op.data?.status;
  useEffect(() => {
    // Once the registry answers, the domain's DNSSEC state has moved on too.
    if (status && status !== "pending")
      void qc.invalidateQueries({ queryKey: domainKeys.detail(orgId, domainId ?? "") });
  }, [status, qc, orgId, domainId]);

  if (detail.isPending || (opId && op.isPending)) return <FullPageSpinner />;
  if (detail.isError) return <ErrorState error={detail.error} onRetry={() => void detail.refetch()} />;
  if (op.isError) return <ErrorState error={op.error} onRetry={() => void op.refetch()} />;

  const d = detail.data.domain;
  const turningOff = op.data ? op.data.kind === "dnssec_off" : d.dnssec === "disabling" || d.dnssec === "off";
  const state: "pending" | "done" | "failed" =
    op.data?.status === "pending"
      ? "pending"
      : op.data && op.data.status !== "completed"
        ? "failed"
        : op.data
          ? "done"
          : d.dnssec === "enabling" || d.dnssec === "disabling"
            ? "pending"
            : "done";
  const back = `/domains/${d.id}?tab=security`;

  const crumb = turningOff ? "Turn off DNSSEC" : "Enable DNSSEC";
  const pill =
    state === "pending" ? (
      <Badge size="md" tone="warning">
        {turningOff ? "Turning off DNSSEC" : "Enabling DNSSEC"}
      </Badge>
    ) : state === "failed" ? (
      <Badge size="md" tone="danger">
        {turningOff ? "Couldn't turn off DNSSEC" : "DNSSEC not enabled"}
      </Badge>
    ) : (
      <Badge size="md" tone={turningOff ? "neutral" : "success"}>
        {turningOff ? "DNSSEC off" : "DNSSEC enabled"}
      </Badge>
    );
  const tile =
    state === "pending" ? (
      <HeadTile tone="pending">
        <DomainIcon name="loading" size={20} className="animate-[spin_2.4s_linear_infinite]" />
      </HeadTile>
    ) : state === "failed" ? (
      <HeadTile tone="danger">
        <TriangleAlert className="size-5" aria-hidden />
      </HeadTile>
    ) : (
      <HeadTile tone="success">
        <CircleCheck className="size-5" aria-hidden />
      </HeadTile>
    );
  const subtitle =
    state === "pending"
      ? turningOff
        ? "Removing cryptographic signing from this domain's DNS"
        : "Setting up cryptographic signing for this domain's DNS"
      : state === "failed"
        ? "The registry didn't accept the change"
        : turningOff
          ? "Your domain's DNS responses are no longer signed"
          : "Your domain's DNS responses are now cryptographically signed";

  const steps: Step[] = turningOff
    ? [
        { label: "DS record removed from registry", state: "in_progress" },
        { label: "Registry propagation", state: "not_yet" },
        { label: "Signing stopped", state: "not_yet" },
      ]
    : [
        { label: "Signing keys generated", state: "completed" },
        { label: "DS record published to registry", state: "completed" },
        { label: "Registry propagation", state: "in_progress" },
        { label: "Verification complete", state: "not_yet" },
      ];

  return (
    <DomainPage>
      <Breadcrumb
        items={[
          { label: "Domains", to: "/domains" },
          { label: "...", to: "/domains" },
          { label: d.display_name, to: back },
          { label: crumb },
        ]}
      />
      <Heading
        size={24}
        tile={tile}
        title={`${d.display_name} - Security`}
        status={pill}
        subtitle={subtitle}
      />

      {state === "pending" && (
        <>
          <Notice tone="processing" title={turningOff ? "Turning off DNSSEC..." : "Enabling DNSSEC..."}>
            {turningOff
              ? "We're removing the DS record from the registry before signing stops. This usually completes within a few minutes."
              : "We're generating signing keys and publishing the DS record to the registry. This usually completes within a few minutes."}
          </Notice>
          <ProgressSection
            title="Setup progress"
            steps={steps}
            footnote="Your domain keeps working normally the entire time — this doesn't affect your website or email."
          />
        </>
      )}

      {state === "failed" && (
        <Notice
          tone="danger"
          title={turningOff ? "DNSSEC couldn't be turned off" : "DNSSEC couldn't be enabled"}
          actions={
            <Link to={back} className={buttonClass("secondary", "lg")}>
              Back to Security overview
            </Link>
          }
        >
          {op.data?.failure ?? "The registry didn't accept the change."} Your domain keeps working as before —
          you can try again from the Security tab.
        </Notice>
      )}

      {state === "done" && (
        <>
          <Notice
            tone={turningOff ? "cream" : "success"}
            title={
              turningOff ? `DNSSEC is off for ${d.display_name}` : `DNSSEC is protecting ${d.display_name}`
            }
          >
            {turningOff
              ? "Your domain keeps working normally. You can turn DNSSEC back on from the Security tab at any time."
              : "Visitors are now protected against DNS spoofing attacks on this domain. No further action needed — CortexGrip manages key rotation automatically."}
          </Notice>
          <Section title="Security status">
            <SecurityStatusRows detail={detail.data} divided />
            <Link to={back} className={buttonClass("secondary", "lg", "self-start")}>
              Back to Security overview
            </Link>
          </Section>
        </>
      )}
    </DomainPage>
  );
}
